import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { reviewArticlePath } from "../consts/seo";
import { previousPublishedReview } from "../content/blog";
import { blogPostBySlug, blogPosts } from "../content/blog-posts";
import { renderMarkdown } from "../content/markdown";
import { NotFoundPage } from "./NotFoundPage";

const reviewBodyClass =
  "review-body space-y-4 text-ink [&_a]:text-accent [&_a]:underline [&_em]:text-sm [&_h2]:mt-8 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-semibold [&_h2_a]:text-inherit [&_h2_a]:no-underline [&_h3]:mt-6 [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-semibold [&_h3_a]:text-inherit [&_h3_a]:no-underline [&_img]:h-auto [&_img]:w-full [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:leading-relaxed [&_p]:text-muted [&_strong]:text-ink [&_table]:w-full [&_table]:text-left [&_table]:text-sm [&_td]:border-t [&_td]:border-line/30 [&_td]:py-2 [&_td]:pr-4 [&_td]:align-top [&_th]:pb-2 [&_th]:pr-4 [&_th]:font-medium [&_th]:text-ink [&_ul]:list-disc [&_ul]:pl-5";

type ExpandedFigure = {
  src: string;
  alt: string;
  caption: string;
  note: string;
};

function visibleFigureImage(figure: HTMLElement): HTMLImageElement | null {
  const dark = document.documentElement.classList.contains("dark");
  const preferred = figure.querySelector<HTMLImageElement>(dark ? "img.for-dark" : "img.for-light");
  if (preferred?.getAttribute("src")) {
    return preferred;
  }
  const images = [...figure.querySelectorAll<HTMLImageElement>("img")];
  return images.find((image) => getComputedStyle(image).display !== "none") ?? images[0] ?? null;
}

export const BlogPost = () => {
  const { slug } = useParams();
  const { hash } = useLocation();
  const post = slug ? blogPostBySlug(slug) : undefined;
  const bodyRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [expanded, setExpanded] = useState<ExpandedFigure | null>(null);

  useLayoutEffect(() => {
    const id = decodeURIComponent(hash.replace(/^#/, ""));
    if (!id) {
      return;
    }
    const scrollToSection = () => {
      document.getElementById(id)?.scrollIntoView();
    };
    scrollToSection();
    const pending = [...document.querySelectorAll<HTMLImageElement>(".review-body img")].filter(
      (image) => !image.complete,
    );
    pending.forEach((image) => image.addEventListener("load", scrollToSection, { once: true }));
    return () => {
      pending.forEach((image) => image.removeEventListener("load", scrollToSection));
    };
  }, [hash, post?.slug]);

  useEffect(() => {
    const root = bodyRef.current;
    if (!root) {
      return;
    }
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const button = target.closest("[data-figure-expand]");
      if (!(button instanceof HTMLButtonElement) || !root.contains(button)) {
        return;
      }
      const figure = button.closest("figure");
      if (!(figure instanceof HTMLElement)) {
        return;
      }
      const image = visibleFigureImage(figure);
      if (!image) {
        return;
      }
      const light = figure.querySelector<HTMLImageElement>("img.for-light");
      setExpanded({
        src: image.currentSrc || image.src,
        alt: image.alt || light?.alt || "Expanded figure",
        caption: figure.querySelector("figcaption")?.textContent?.trim() ?? "",
        note: figure.querySelector(".figure-note")?.textContent?.trim() ?? "",
      });
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [post?.slug]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    if (expanded && !dialog.open) {
      if (typeof dialog.showModal === "function") {
        dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
    }
    if (!expanded && dialog.open) {
      dialog.close();
    }
  }, [expanded]);

  if (!post) {
    return <NotFoundPage />;
  }

  const html = renderMarkdown(post.body);
  const locked = post.status === "coming-soon";
  const previous = locked ? previousPublishedReview(post, blogPosts) : undefined;
  const body = (
    <div ref={bodyRef} className={`${reviewBodyClass} mt-8`} dangerouslySetInnerHTML={{ __html: html }} />
  );

  return (
    <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <p className="section-label">Review</p>
      <h1 className="mt-2 font-display text-4xl font-semibold text-ink">{post.title}</h1>
      <p className="mt-3 text-sm text-muted">
        <time dateTime={post.datePublished}>{post.datePublished}</time>
        {" · "}
        {post.author}
        {locked ? " · Coming soon" : ""}
      </p>
      {locked ? (
        <div className="review-locked">
          <div className="review-locked-note">
            <p>This review is coming soon.</p>
            {previous ? (
              <p>
                <Link to={reviewArticlePath(previous.slug)}>Read the previous article</Link>
              </p>
            ) : null}
          </div>
          {body}
        </div>
      ) : (
        body
      )}
      <dialog
        ref={dialogRef}
        className="figure-dialog"
        aria-label={expanded?.caption || "Expanded figure"}
        onClose={() => setExpanded(null)}
        onClick={(event) => {
          if (event.target === dialogRef.current) {
            dialogRef.current?.close();
          }
        }}
      >
        <div className="figure-dialog-bar">
          <button type="button" className="figure-dialog-close" onClick={() => dialogRef.current?.close()}>
            Close
          </button>
        </div>
        {expanded ? <img src={expanded.src} alt={expanded.alt} /> : null}
        {expanded?.note ? <p className="figure-note">{expanded.note}</p> : null}
      </dialog>
      <p className="mt-10 text-sm text-muted">
        <Link to="/explore" className="text-accent underline">
          Explore blocking maps
        </Link>
        {" · "}
        <Link to="/science" className="text-accent underline">
          Science and glossary
        </Link>
      </p>
    </article>
  );
};
